import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Ticket, Inbox, Users, Shield, User as UserIcon,
  LogOut, Sparkles, Command, Sun, Moon, Search, ChevronRight, Server, Bot,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/hooks/useTheme";
import NotificationBell from "@/components/NotificationBell";
import AnnouncementBanner from "@/components/AnnouncementBanner";
import CommandPalette from "@/components/CommandPalette";

const NAV_CUSTOMER = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard },
  { to: "/tickets", label: "My Tickets", icon: Ticket },
  { to: "/servers", label: "My Servers", icon: Server },
  { to: "/assistant", label: "DrkCECO", icon: Bot },
  { to: "/notifications", label: "Inbox", icon: Inbox },
  { to: "/profile", label: "Profile", icon: UserIcon },
];

const NAV_STAFF = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard },
  { to: "/queue", label: "Queue", icon: Ticket },
  { to: "/tickets", label: "My Tickets", icon: Users },
  { to: "/assistant", label: "DrkCECO", icon: Bot },
  { to: "/notifications", label: "Inbox", icon: Inbox },
  { to: "/profile", label: "Profile", icon: UserIcon },
];

const NAV_ADMIN = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard },
  { to: "/queue", label: "Queue", icon: Ticket },
  { to: "/admin", label: "Admin", icon: Shield },
  { to: "/assistant", label: "DrkCECO", icon: Bot },
  { to: "/notifications", label: "Inbox", icon: Inbox },
  { to: "/profile", label: "Profile", icon: UserIcon },
];

export default function AppShell() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [paletteOpen, setPaletteOpen] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const role = user?.role || "customer";
  const nav = role === "admin" ? NAV_ADMIN : role === "agent" ? NAV_STAFF : NAV_CUSTOMER;

  return (
    <div className="relative min-h-screen bg-background text-foreground">
      <div className="grain-overlay" aria-hidden />
      <AnnouncementBanner />
      <div className="flex">
        {/* Sidebar */}
        <aside className="hidden md:flex flex-col w-64 border-r border-border/60 min-h-screen sticky top-0 bg-background/60 backdrop-blur-xl z-20">
          <div className="p-6 border-b border-border/60 flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-md bg-primary text-primary-foreground flex items-center justify-center font-display font-black">L</div>
            <div className="flex flex-col leading-tight">
              <span className="font-display font-black text-lg tracking-tighter">LostDrkC</span>
              <span className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground">Support Core</span>
            </div>
          </div>
          <nav className="flex-1 p-3 space-y-1">
            {nav.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
                end={to === "/dashboard"}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                    isActive
                      ? "bg-primary/10 text-primary border-l-2 border-primary"
                      : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                  }`
                }
              >
                <Icon className="h-4 w-4" />
                <span>{label}</span>
              </NavLink>
            ))}
          </nav>
          <div className="p-3 border-t border-border/60 space-y-2">
            <button
              data-testid="open-command-palette-btn"
              onClick={() => setPaletteOpen(true)}
              className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md text-xs text-muted-foreground border border-border/60 hover:border-primary/50 transition-colors"
            >
              <span className="flex items-center gap-2"><Search className="h-3.5 w-3.5" /> Command</span>
              <span className="font-mono uppercase tracking-widest">⌘K</span>
            </button>
            <div className="flex items-center gap-2 px-1 pt-2">
              <div className="h-8 w-8 rounded-full bg-secondary flex items-center justify-center text-xs font-semibold">
                {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium truncate">{user?.name || user?.email}</div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{role}</div>
              </div>
              <Button data-testid="logout-btn" size="icon" variant="ghost" onClick={async () => { await logout(); navigate("/"); }}>
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </aside>

        {/* Main */}
        <main className="flex-1 relative z-10 min-w-0">
          {/* Topbar */}
          <header className="sticky top-0 z-30 h-14 border-b border-border/60 bg-background/70 backdrop-blur-xl flex items-center gap-3 px-4 md:px-8">
            <div className="md:hidden flex items-center gap-2">
              <div className="h-7 w-7 rounded-md bg-primary text-primary-foreground flex items-center justify-center font-display font-black text-sm">L</div>
              <span className="font-display font-black">LostDrkC</span>
            </div>
            <button
              onClick={() => setPaletteOpen(true)}
              className="hidden md:flex items-center gap-2 h-9 px-3 rounded-md border border-border/60 text-muted-foreground text-xs hover:border-primary/50 transition-colors min-w-[260px]"
              data-testid="topbar-search-btn"
            >
              <Search className="h-3.5 w-3.5" />
              <span>Jump to ticket, page, action…</span>
              <span className="ml-auto font-mono uppercase tracking-widest">⌘K</span>
            </button>
            <div className="ml-auto flex items-center gap-2">
              <Button data-testid="theme-toggle" size="icon" variant="ghost" onClick={toggle} aria-label="Toggle theme">
                {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </Button>
              <NotificationBell />
              <Button
                data-testid="new-ticket-btn"
                size="sm"
                onClick={() => navigate("/tickets/new")}
                className="gap-1.5"
              >
                <Sparkles className="h-3.5 w-3.5" />
                New ticket
              </Button>
            </div>
          </header>

          <div className="px-4 md:px-8 py-8 max-w-[1400px]">
            <Outlet />
          </div>
        </main>
      </div>
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </div>
  );
}
